import { CreditCardDataFiltered } from 'data/shared/CreditCardData';
import { flattenRewardsEarnRates } from 'data/shared/utils/flattenRewardsEarnRates';
import { CardData } from 'data/types';
import { CategoryType } from 'data/shared/__autogenerated__/types/CreditCardCategoryTypes';
import { formatSpendCategory } from 'data/shared/utils/formatSpendCategory';
import { categoryConfigurations } from 'data/shared/utils/categoryConfig';

export type CategoryMatch = {
  categoryKey: string;
  displayName: string;
  matchType: 'exact' | 'partial' | 'alias';
  matchScore: number;
  emoji?: string;
};

// Build emoji lookup from categoryConfigurations
const CATEGORY_EMOJI_MAP = new Map<string, string>();
categoryConfigurations.forEach((config) => {
  if (config.emoji) {
    CATEGORY_EMOJI_MAP.set(config.key, config.emoji);
  }
});

// Get emoji for a category key
function getCategoryEmoji(categoryKey: string): string | undefined {
  return CATEGORY_EMOJI_MAP.get(categoryKey);
}

// Build searchable category data from CategoryType enum
const SEARCHABLE_CATEGORIES = Object.values(CategoryType).map((key) => {
  const displayName = formatSpendCategory(key);
  const emoji = getCategoryEmoji(key);

  // Build search terms from the display name and key
  const searchTerms = [key.toLowerCase(), displayName.toLowerCase()];

  // Add common aliases based on category
  if (key.includes('gas')) {
    searchTerms.push('fuel', 'gasoline', 'petrol');
  }
  if (key.includes('grocery')) {
    searchTerms.push('supermarket', 'food shopping');
  }
  if (key.includes('dining')) {
    searchTerms.push('restaurant', 'food', 'eat');
  }
  if (key.includes('travel')) {
    searchTerms.push('vacation', 'trip');
  }
  if (key.includes('airline')) {
    searchTerms.push('flight', 'plane');
  }
  if (key.includes('hotel')) {
    searchTerms.push('lodging', 'accommodation');
  }
  if (key.includes('rideshare')) {
    searchTerms.push('uber', 'lyft');
  }
  if (key.includes('streaming')) {
    searchTerms.push('netflix', 'spotify', 'hulu');
  }
  if (key.includes('drugstore') || key.includes('pharmacy')) {
    searchTerms.push('cvs', 'walgreens');
  }
  if (key.includes('onlineShopping')) {
    searchTerms.push('ecommerce', 'internet shopping');
  }
  if (key.includes('amazon')) {
    searchTerms.push('amazon');
  }
  if (key.includes('homeImprovement')) {
    searchTerms.push('hardware', 'diy', 'home depot', 'lowes');
  }
  if (key.includes('wholesaleClub')) {
    searchTerms.push('warehouse', 'costco', 'bulk');
  }
  if (key.includes('fitness')) {
    searchTerms.push('gym', 'workout', 'exercise');
  }

  return {
    key,
    displayName,
    emoji,
    searchTerms: Array.from(new Set(searchTerms)), // Remove duplicates
  };
});

/**
 * Matches a search query to category names
 * Returns matches ordered by relevance
 */
export function matchCategories(query: string): CategoryMatch[] {
  if (!query || query.length < 2) {
    return [];
  }

  const lowerQuery = query.toLowerCase().trim();
  const matches: CategoryMatch[] = [];

  SEARCHABLE_CATEGORIES.forEach(({ key, displayName, emoji, searchTerms }) => {
    const lowerDisplayName = displayName.toLowerCase();

    // Exact match on display name
    if (lowerDisplayName === lowerQuery) {
      matches.push({
        categoryKey: key,
        displayName,
        matchType: 'exact',
        matchScore: 100,
        emoji,
      });
      return;
    }

    // Check search terms for exact match
    if (searchTerms.some((term) => term === lowerQuery)) {
      matches.push({
        categoryKey: key,
        displayName,
        matchType: 'exact',
        matchScore: 95,
        emoji,
      });
      return;
    }

    // Partial match - display name starts with query
    if (lowerDisplayName.startsWith(lowerQuery)) {
      matches.push({
        categoryKey: key,
        displayName,
        matchType: 'partial',
        matchScore: 80,
        emoji,
      });
      return;
    }

    // Partial match - search term starts with query
    if (searchTerms.some((term) => term.startsWith(lowerQuery))) {
      matches.push({
        categoryKey: key,
        displayName,
        matchType: 'partial',
        matchScore: 70,
        emoji,
      });
      return;
    }

    // Contains match - display name contains query
    if (lowerDisplayName.includes(lowerQuery)) {
      matches.push({
        categoryKey: key,
        displayName,
        matchType: 'partial',
        matchScore: 50,
        emoji,
      });
      return;
    }

    // Contains match - any search term contains query
    if (searchTerms.some((term) => term.includes(lowerQuery))) {
      matches.push({
        categoryKey: key,
        displayName,
        matchType: 'partial',
        matchScore: 40,
        emoji,
      });
    }
  });

  // Sort by match score (highest first)
  return matches.sort((a, b) => b.matchScore - a.matchScore);
}

/**
 * Gets cards that have good earn rates for a specific category
 * Returns cards sorted by earn rate (highest first)
 */
export function getCardsForCategory(
  categoryKey: string,
  minEarnRate = 2
): CardData[] {
  const cardsWithEarnRates: Array<{ card: CardData; earnRate: number }> = [];

  Object.values(CreditCardDataFiltered).forEach((card) => {
    if (!card.rewards_earn_rates || !Array.isArray(card.rewards_earn_rates)) {
      return;
    }

    // Flatten the earn rates for this card
    const flattenedRates = flattenRewardsEarnRates(card.rewards_earn_rates);

    // Find the best earn rate for the specified category
    let bestEarnRate = 0;

    flattenedRates.forEach((rate) => {
      const rateCategory = rate.category;

      // Check if this rate applies to the category
      // Support both exact matches and parent category matches
      // e.g., "travel" should match "travel.airline"
      const isMatch =
        rateCategory === categoryKey ||
        rateCategory.startsWith(`${categoryKey}.`) ||
        categoryKey.startsWith(`${rateCategory}.`);

      if (isMatch && rate.earnRate > bestEarnRate) {
        bestEarnRate = rate.earnRate;
      }
    });

    if (bestEarnRate >= minEarnRate) {
      cardsWithEarnRates.push({ card, earnRate: bestEarnRate });
    }
  });

  // Sort by earn rate (highest first), then by card name
  return cardsWithEarnRates
    .sort((a, b) => {
      if (b.earnRate !== a.earnRate) {
        return b.earnRate - a.earnRate;
      }
      return a.card.name.localeCompare(b.card.name);
    })
    .map((item) => item.card);
}

/**
 * Gets the display name for a category key
 */
export function getCategoryDisplayName(categoryKey: string): string {
  return formatSpendCategory(categoryKey);
}
